import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import { SlimButton, DatePicker, Alert, Icon, Radio } from 'components'
import { useForm, Controller, useFormContext } from 'react-hook-form'
import { useDispatch } from 'store/store'
import {
  getAnalysisReport,
  getPendingAnalysisReport
} from 'store/ExtraVolumeContext/ExtraVolumeSlice'
import moment from 'moment'

import * as S from './styles'

type ReportModalProps = {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
}

type ReportKind = 'pending' | 'history'

const ModalReport = ({ open, setOpen }: ReportModalProps) => {
  const dispatch = useDispatch()
  const [selectedReport, setSelectedReport] = useState<ReportKind>('pending')
  const defaultValues = {
    beginDate: moment().subtract(1, 'year').format('YYYY-MM'),
    endDate: moment().format('YYYY-MM')
  }

  const { getValues: getFilterValues } = useFormContext()

  const { control, trigger, handleSubmit, getValues, reset, formState } = useForm({
    defaultValues,
    mode: 'onChange'
  })

  const handleClose = () => {
    setOpen(false)
    setSelectedReport('pending')
    reset(defaultValues)
  }

  const submitForm = handleSubmit((data) => {
    if (selectedReport === 'pending') {
      const {
        EquipeVenda,
        EscritorioVenda,
        Materias,
        Mercado,
        MesesCota,
        RegiaoVenda,
        TipoCritica
      } = getFilterValues()
      dispatch(
        getPendingAnalysisReport({
          EquipeVenda,
          EscritorioVenda,
          Materias,
          Mercado,
          MesesCota,
          RegiaoVenda,
          TipoCritica
        })
      )
    } else {
      const payload = {
        dt_ini: moment(data.beginDate).format('YYYY-MM-DD HH:mm:ss'),
        dt_fim: moment(data.endDate).endOf('month').format('YYYY-MM-DD HH:mm:ss')
      }
      dispatch(getAnalysisReport(payload))
    }
    handleClose()
  })

  const isValidInterval = useMemo(() => {
    const { beginDate, endDate } = getValues()
    if (formState.errors.beginDate || formState.errors.endDate) return true
    return moment(endDate).diff(moment(beginDate), 'month') <= 12
  }, [formState])

  return (
    <>
      {open && (
        <ModalContentLayout
          closeAction={handleClose}
          title="Baixar relatório"
          titleSize={24}
        >
          <S.Content>
            <S.RadioGroup>
              <Radio
                label="Análises pendentes"
                value="pending"
                name="report_type"
                checked={selectedReport === 'pending'}
                onChange={() => {
                  setSelectedReport('pending')
                  reset(defaultValues)
                }}
              />
              <Radio
                label="Histórico"
                value="history"
                name="report_type"
                checked={selectedReport === 'history'}
                onChange={() => setSelectedReport('history')}
              />
            </S.RadioGroup>

            {selectedReport === 'history' && (
              <>
                <S.Divider />
                <span>Informe um período de até 1 ano:</span>
                <S.FieldsWrapper>
                  <Controller
                    name="beginDate"
                    control={control}
                    rules={{
                      validate: {
                        min: (date) =>
                          moment(date)
                            .endOf('month')
                            .isAfter(moment().subtract(1, 'year').startOf('month')) ||
                          'Data inválida.'
                      }
                    }}
                    render={({ field, fieldState }) => (
                      <DatePicker
                        {...field}
                        height={30}
                        minDate={moment().subtract(1, 'year') as unknown as string}
                        maxDate={moment().add(2, 'year') as unknown as string}
                        label="Mês cota inicial"
                        onChange={(date) => {
                          field.onChange(moment(date).format('YYYY-MM'))
                          trigger()
                        }}
                        error={fieldState.error?.message}
                        errorState={!isValidInterval}
                        views={['year', 'month']}
                        inputFormat="MM/YYYY"
                        disableMaskedInput={false}
                      />
                    )}
                  />
                  <Controller
                    name="endDate"
                    control={control}
                    rules={{
                      validate: {
                        min: (date) =>
                          moment(date)
                            .endOf('month')
                            .isAfter(moment().subtract(1, 'year').startOf('month')) ||
                          'Data inválida.',
                        afterBegin: (date) =>
                          moment(date)
                            .endOf('month')
                            .isAfter(moment(getValues('beginDate')).startOf('month')) ||
                          'Período inválido.'
                      }
                    }}
                    render={({ field, fieldState }) => (
                      <DatePicker
                        {...field}
                        height={30}
                        minDate={moment().subtract(1, 'year') as unknown as string}
                        maxDate={moment().add(2, 'year') as unknown as string}
                        label="Mês cota final"
                        onChange={(date) => {
                          field.onChange(moment(date).format('YYYY-MM'))
                          trigger()
                        }}
                        error={fieldState.error?.message}
                        errorState={!isValidInterval}
                        views={['year', 'month']}
                        inputFormat="MM/YYYY"
                        disableMaskedInput={false}
                      />
                    )}
                  />
                </S.FieldsWrapper>

                {!isValidInterval && (
                  <Alert
                    fullWidth
                    kind="error"
                    title="O período deve ser inferior a 1 ano"
                  />
                )}
              </>
            )}
          </S.Content>

          <S.Footer>
            <S.ButtonWrapper>
              <SlimButton
                content="Voltar"
                variant="secondary"
                onClick={handleClose}
                icon={<Icon icon="undo" width="15" />}
              />
            </S.ButtonWrapper>
            <S.ButtonWrapper>
              <SlimButton
                content="Baixar"
                variant="primary"
                onClick={submitForm}
                icon={<Icon icon="download" width="15" />}
                disabled={
                  selectedReport !== 'pending' && (!isValidInterval || !formState.isValid)
                }
              />
            </S.ButtonWrapper>
          </S.Footer>
        </ModalContentLayout>
      )}
    </>
  )
}

export default ModalReport
